/**
 * @file components/ReSignIn.js
 * @description The "Sign in again" door: a small card that lets a signed-in
 * person start a FRESH session without leaving the page they are on.
 *
 * THE RULE THIS DOES NOT BEND (James, 2026-09-08): a Butler Core session is a
 * 24-hour absolute timeout with no sliding renewal. This card does not renew
 * anything. It is a DELIBERATE re-sign-in — the password is required every
 * single time — and a successful one mints a brand-new 24-hour session. No
 * click, no page view and no background call extends a session on its own.
 *
 * Shape (fractal modularity, memory 20): this file owns the whole feature and
 * exposes exactly two things, so nothing has to import anything else:
 *   - openReSignIn()   any screen can knock on the door (a browser event)
 *   - <ReSignInHost /> mounted ONCE in App.js; it listens and draws the card
 * The banner (OfflineIndicator) and the avatar menu (Navbar) therefore do not
 * know about each other, and neither one owns the card.
 *
 * What a success does, in order:
 *   1. POST the existing login endpoint (rate limits and lockout unchanged)
 *   2. re-read /auth/me so the new deadline comes from the SERVER, not from us
 *   3. hand that session to the offline queue, which clears the orange strip
 *      and resumes any held flush — all with no reload, so nothing on screen
 *      is disturbed and no in-progress work is lost.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Input, Button, useToast } from "../design-system";
import { useAuth } from "../contexts/AuthContext";
import { notifySignedIn } from "../offline/syncEngine";
import { API } from "../lib/api";
import { signInFailureMessage } from "../lib/signInMessage";

/** The browser event both doors knock on. Internal to this file. */
const OPEN_EVENT = "butler:re-sign-in";

/** How many wrong passwords before we stop and send them to the login page. */
const MAX_ATTEMPTS = 3;

/**
 * Open the "Sign in again" card from anywhere in the app.
 * Safe to call when the host is not mounted — nothing happens.
 * @returns {void}
 */
export function openReSignIn() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

/**
 * The card itself. Mount ONE of these, high in the tree (App.js).
 * Renders nothing until someone calls openReSignIn().
 */
export function ReSignInHost() {
  const auth = useAuth();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const attemptsRef = useRef(0);
  const passwordRef = useRef(null);

  const email = (auth && auth.user && auth.user.email) || "";

  useEffect(() => {
    function onOpen() {
      attemptsRef.current = 0;
      setPassword("");
      setError("");
      setBusy(false);
      setOpen(true);
    }
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  // Put the cursor in the password box — the email is already known and locked.
  // The Modal takes focus for its own first control on the next animation
  // frame, and the field does not exist on the very first tick, so we reach
  // for it three times across the opening animation and stop as soon as it
  // has focus. Cheap, and it survives a slow frame on a phone.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const grab = () => {
      if (cancelled) return;
      const el = passwordRef.current;
      if (el && el.focus && document.activeElement !== el) el.focus();
    };
    const timers = [0, 80, 250].map((ms) => setTimeout(grab, ms));
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [open]);

  const close = useCallback(() => {
    setOpen(false);
    setPassword("");
    setError("");
    setBusy(false);
  }, []);

  const submit = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (busy) return;
    if (!password) {
      setError("Please enter your password.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      // The SAME endpoint the login page uses: same rate limit, same lockout,
      // same generic answer. A new 24-hour window starts here and nowhere else.
      await auth.login(email, password);

      // Ask the server what the session actually is now, rather than trusting
      // the login answer. If the cookie did not stick, this is where we find
      // out — and the strip keeps telling the truth either way.
      let session = null;
      try {
        const res = await auth.authFetch(`${API}/auth/me`);
        if (res.ok) {
          const data = await res.json();
          session = (data && data.session) || null;
        }
      } catch (_) { /* the login itself succeeded; the banner just refreshes later */ }
      if (session) notifySignedIn(session);

      close();
      toast.success("Signed in — fresh 24 hours");
    } catch (err) {
      attemptsRef.current += 1;
      const message = signInFailureMessage(err);
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        close();
        toast.error("Still not signing in. Use the full sign-in page.");
        return;
      }
      setError(message);
      setPassword("");
      setBusy(false);
      if (passwordRef.current && passwordRef.current.focus) passwordRef.current.focus();
    }
  }, [auth, busy, close, email, password, toast]);

  if (!open) return null;

  // No email means we do not know who is at the keyboard — send them to the
  // real login page rather than guessing an identity.
  if (!email) {
    return (
      <Modal open onClose={close} size="sm" title="Sign in again">
        <p style={{ margin: 0, color: "var(--text-secondary)", fontFamily: "var(--font-family)" }}>
          Your sign-in has ended. Open the sign-in page to start a fresh 24 hours.
        </p>
        <div style={{ marginTop: "var(--space-4)" }}>
          <Button variant="primary" size="lg" fullWidth as="a" href="/app/login">
            Go to sign-in
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open onClose={busy ? () => {} : close} size="sm" title="Sign in again">
      <form onSubmit={submit}>
        <p style={{ marginTop: 0, marginBottom: "var(--space-4)", color: "var(--text-secondary)", fontSize: "var(--text-body-sm)", fontFamily: "var(--font-family)" }}>
          Signing in again starts a fresh 24 hours. Your password is required every
          time — nothing extends a sign-in on its own.
        </p>

        <Input
          type="email"
          label="Signed in as"
          value={email}
          readOnly
          disabled
          autoComplete="username"
          data-testid="resignin-email"
        />

        <div style={{ marginTop: "var(--space-3)" }}>
          <Input
            ref={passwordRef}
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={error || undefined}
            autoComplete="current-password"
            data-testid="resignin-password"
          />
        </div>

        <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-5)" }}>
          <Button type="button" variant="outline" size="lg" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="lg" fullWidth loading={busy} data-testid="resignin-submit">
            Sign in
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default ReSignInHost;
